import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Paper, 
  Grid, 
  Card, 
  CardContent, 
  Box,
  useTheme,
  alpha,
  IconButton,
  Tooltip,
  Fade,
  ButtonGroup,
  Button,
  Stack,
  Popover,
  Alert,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Container,
  Divider
} from '@mui/material';
import { 
  Refresh as RefreshIcon,
  MonetizationOn as MonetizationOnIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  LocalOffer as LocalOfferIcon,
  People as PeopleIcon,
  ShoppingCart as ShoppingCartIcon,
  CalendarToday as CalendarTodayIcon,
  CheckCircle as CheckCircleIcon,
  TrendingUp as TrendingUpIcon,
  Analytics as AnalyticsIcon,
  Dashboard as DashboardIcon,
  Timeline as TimelineIcon,
  LocationCity as LocationCityIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DateTimePicker, StaticDateTimePicker } from '@mui/x-date-pickers';
import { ru } from 'date-fns/locale';
import axiosInstance from '../utils/axios'; // Import axiosInstance
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { formatCurrency } from '../utils/formatters';
import { startOfDay, endOfDay, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subYears, addYears } from 'date-fns';
import { getStats, getProductsStats, getIssuedStats, getCryptoStats } from '../services/statsService';

const PositionsPanel = ({ title, data, isLoading, color = 'primary.main', icon }) => {
  const formatCurrency = (value) => new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', minimumFractionDigits: 0 }).format(value || 0);

  return (
    <Card 
      elevation={0}
      sx={{
        height: '100%',
        minWidth: 0,
        borderRadius: 2,
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider'
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Box display="flex" alignItems="center" mb={2}>
          <Box 
            sx={{ 
              color: color,
              mr: 1.5
            }}
          >
            {React.cloneElement(icon, { sx: { fontSize: 24 } })}
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" color={color}>
              {title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {data.positions?.length || 0} позиций
            </Typography>
          </Box>
        </Box>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 150 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            <Box sx={{ minHeight: 120, maxHeight: 200, overflowY: 'auto' }}>
              {data.positions && data.positions.length > 0 ? (
                <Stack spacing={0.5}>
                  {data.positions.map((pos, index) => (
                    <Box 
                      key={pos.packagingId || index}
                      sx={{ 
                        p: 1,
                        bgcolor: 'background.default',
                        borderRadius: 1,
                        border: 1,
                        borderColor: 'divider'
                      }}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            flexGrow: 1,
                            fontWeight: 'medium',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {pos.productName ? `${pos.packagingName} • ${pos.productName}` : pos.packagingName}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={0.5} sx={{ flexShrink: 0 }}>
                          <Typography variant="caption" sx={{ fontWeight: 'medium', minWidth: '80px', textAlign: 'right', color: 'text.secondary' }}>
                            {pos.volume && pos.totalVolume ? `${pos.count} × ${pos.volume}г = ${pos.totalVolume}г` : `${pos.count} шт.`}
                          </Typography>
                          <Typography variant="caption" sx={{ fontWeight: 'bold', minWidth: '70px', textAlign: 'right' }}>
                            {formatCurrency(pos.sum)}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Box 
                  sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 120,
                    opacity: 0.5
                  }}
                >
                  <Typography variant="body2" color="text.secondary">Нет данных</Typography>
                </Box>
              )}
            </Box>
            
            <Divider sx={{ my: 1.5 }} />
            
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle2" fontWeight="bold">
                Итого
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2" fontWeight="bold">
                  {data.totalCount} шт.
                </Typography>
                <Typography variant="body2" fontWeight="medium" color="text.secondary">
                  {data.totalVolume ? `${data.totalVolume} г` : '-'}
                </Typography>
                <Typography variant="h6" fontWeight="bold" color={color}>
                  {formatCurrency(data.totalSum)}
                </Typography>
              </Box>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

const PERIOD_PRESETS = [
  { label: 'Сегодня', getValue: () => ({
    start: startOfDay(new Date()),
    end: endOfDay(new Date())
  })},
  { label: 'Вчера', getValue: () => ({
    start: startOfDay(subDays(new Date(), 1)),
    end: endOfDay(subDays(new Date(), 1))
  })},
  { label: 'Неделя', getValue: () => ({
    start: startOfWeek(new Date(), { locale: ru }),
    end: endOfWeek(new Date(), { locale: ru })
  })},
  { label: 'Месяц', getValue: () => ({
    start: startOfMonth(new Date()),
    end: endOfMonth(new Date())
  })},
  { label: '7 дней', getValue: () => ({
    start: startOfDay(subDays(new Date(), 6)),
    end: endOfDay(new Date())
  })},
  { label: '30 дней', getValue: () => ({
    start: startOfDay(subDays(new Date(), 29)),
    end: endOfDay(new Date())
  })}
];

const Dashboard = () => {
  const { currentUser } = useAuth();
  const theme = useTheme();
  const [startDate, setStartDate] = useState(startOfDay(new Date()));
  const [endDate, setEndDate] = useState(endOfDay(new Date()));
  const [selectedCity, setSelectedCity] = useState('');
  const [stats, setStats] = useState({
    totalSum: 0,
    totalBaseSum: 0,
    totalCuponSum: 0,
    ordersCount: 0,
    usersCount: 0,
    marketplaceSold: {
      totalSum: 0,
      totalBaseSum: 0,
      totalCuponSum: 0,
      ordersCount: 0
    }
  });
  const [isLoading, setIsLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [activePeriod, setActivePeriod] = useState('Сегодня');
  const [productsStats, setProductsStats] = useState([]);
  const [isProductsLoading, setIsProductsLoading] = useState(false);
  const [couriers, setCouriers] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedCourier, setSelectedCourier] = useState('');
  const [addressStats, setAddressStats] = useState({
    active: { totalCount: 0, totalSum: 0, totalVolume: 0, positions: [] },
    inactive: { totalCount: 0, totalSum: 0, totalVolume: 0, positions: [] },
  });
  const [addressLoading, setAddressLoading] = useState(false);
  const [issuedStats, setIssuedStats] = useState({
    totalCount: 0,
    totalAmount: 0,
    packagings: []
  });
  const [issuedLoading, setIssuedLoading] = useState(false);
  const [cryptoStats, setCryptoStats] = useState({ rows: [], totalBase: 0, count: 0 });
  const [cryptoLoading, setCryptoLoading] = useState(false);

  const handlePeriodClick = (preset) => {
    const { start, end } = preset.getValue();
    setStartDate(start);
    setEndDate(end);
    setActivePeriod(preset.label);
  };

  const handleCustomDateClick = (event) => {
    setAnchorEl(event.currentTarget);
    setActivePeriod('Другой');
  };

  const handleCustomDateClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    const fetchStats = async () => {
      setIsLoading(true);
      setIsProductsLoading(true);
      setIssuedLoading(true);
      setCryptoLoading(true);
      try {
        const [statsResponse, productsResponse, issuedResponse, cryptoResponse] = await Promise.all([
          getStats(startDate, endDate, selectedCourier, selectedCity),
          getProductsStats(selectedCourier, selectedCity),
          getIssuedStats(startDate, endDate, selectedCourier, selectedCity),
          getCryptoStats(startDate, endDate)
        ]);
        
        // Обработка основной статистики
        if (statsResponse) {
          setStats({
            totalSum: statsResponse.totalSum || 0,
            totalBaseSum: statsResponse.totalBaseSum || 0,
            totalCuponSum: statsResponse.totalCuponSum || 0,
            ordersCount: statsResponse.ordersCount || 0,
            usersCount: statsResponse.usersCount || 0,
            marketplaceSold: {
              totalSum: statsResponse.marketplaceSold?.totalSum || 0,
              totalBaseSum: statsResponse.marketplaceSold?.totalBaseSum || 0,
              totalCuponSum: statsResponse.marketplaceSold?.totalCuponSum || 0,
              ordersCount: statsResponse.marketplaceSold?.ordersCount || 0,
            }
          });
        }
        
        // Обработка статистики по товарам
        let processedProducts = [];
        if (Array.isArray(productsResponse)) {
          processedProducts = productsResponse.map(product => ({
            ...product,
            id: product.productId || Math.random().toString(36).substr(2, 9),
            totalItems: product.totalItems || 0,
            totalAmount: product.totalAmount || 0,
            packagings: Array.isArray(product.packagings) 
              ? product.packagings.map(pkg => ({
                  ...pkg,
                  packagingId: pkg.packagingId || Math.random().toString(36).substr(2, 9),
                  name: pkg.name || 'Фасовка',
                  weight: pkg.weight || 0,
                  price: pkg.price || 0,
                  count: pkg.count || 0,
                  totalAmount: pkg.totalAmount || 0
                }))
              : []
          }));
        }
        
        setProductsStats(processedProducts);
        
        // Обработка статистики выданных адресов
        if (issuedResponse) {
          setIssuedStats({
            totalCount: issuedResponse.totalCount || 0,
            totalAmount: issuedResponse.totalAmount || 0,
            packagings: issuedResponse.packagings || []
          });
        }

        // Обработка крипто-статистики
        if (cryptoResponse && cryptoResponse.success) {
          setCryptoStats({
            rows: Array.isArray(cryptoResponse.rows) ? cryptoResponse.rows : [],
            totalBase: cryptoResponse.totalBase || 0,
            count: cryptoResponse.count || 0
          });
        } else {
          setCryptoStats({ rows: [], totalBase: 0, count: 0 });
        }
      } catch (error) {
        console.error('Error fetching stats:', error);
        setProductsStats([]);
        setIssuedStats({ totalCount: 0, totalAmount: 0, packagings: [] });
        setCryptoStats({ rows: [], totalBase: 0, count: 0 });
      } finally {
        setIsLoading(false);
        setIsProductsLoading(false);
        setIssuedLoading(false);
        setCryptoLoading(false);
      }
    };

    fetchStats();
  }, [startDate, endDate, selectedCourier, selectedCity, refreshKey]);

  useEffect(() => {
    const fetchData = async () => {
      setAddressLoading(true);
      try {
        // Параметры для запроса статистики
        const statsParams = {
          withStats: true,
        };
        if (startDate) {
          statsParams.dateFrom = startDate.toISOString();
        }
        if (endDate) {
          statsParams.dateTo = endDate.toISOString();
        }
        if (selectedCourier) {
          statsParams.courier = selectedCourier;
        }
        if (selectedCity) {
          statsParams.city = selectedCity;
        }

        // Одновременный запрос курьеров и статистики, как в Addresses.js
        const [couriersResponse, statsResponse, citiesResponse] = await Promise.all([
          axiosInstance.get('/staff'), // Получаем всех сотрудников
          axiosInstance.get('/addresses/stats', { params: statsParams }),
          axiosInstance.get('/cities')
        ]);

        // 1. Обрабатываем и фильтруем курьеров
        const allStaff = couriersResponse.data.data || [];
        const onlyCouriers = allStaff.filter(staff => staff.role === 'courier');
        setCouriers(onlyCouriers);

        // 2. Обрабатываем города
        const allCities = citiesResponse.data.data || [];
        setCities(allCities);

        // 2. Обрабатываем статистику
        const statsData = statsResponse.data || {};
        setAddressStats({
          active: statsData.active || { totalCount: 0, totalSum: 0, totalVolume: 0, positions: [] },
          inactive: statsData.inactive || { totalCount: 0, totalSum: 0, totalVolume: 0, positions: [] },
        });

      } catch (error) {
        console.error('Ошибка загрузки данных:', error.response?.data?.message || error.message);
        // Сбрасываем данные при ошибке
        setCouriers([]);
        setAddressStats({
          active: { totalCount: 0, totalSum: 0, totalVolume: 0, positions: [] },
          inactive: { totalCount: 0, totalSum: 0, totalVolume: 0, positions: [] },
        });
      } finally {
        setAddressLoading(false);
      }
    };

    fetchData();
  }, [startDate, endDate, selectedCourier, selectedCity]);

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  const productColumns = [
    { 
      field: 'productName', 
      headerName: 'Товар', 
      flex: 2,
      renderCell: (params) => {
        const row = params.row || {};
        return (
          <Box sx={{ fontWeight: 'bold' }}>{row.productName || 'Без названия'}</Box>
        );
      }
    },
    { 
      field: 'packagings', 
      headerName: 'Фасовки и города', 
      flex: 4,
      sortable: false,
      renderCell: (params) => {
        const row = params.row || {};
        const packagings = Array.isArray(row.packagings) ? row.packagings : [];
        
        return (
          <Box sx={{ width: '100%' }}>
            {packagings.length > 0 ? (
              packagings.map((pkg, index) => (
                <Box 
                  key={`${pkg.packagingId || ''}-${index}`}
                  sx={{ 
                    mb: index < packagings.length - 1 ? 2 : 0,
                    pb: index < packagings.length - 1 ? 2 : 0,
                    borderBottom: index < packagings.length - 1 ? `1px solid ${theme.palette.divider}` : 'none'
                  }}
                >
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    mb: 1,
                    alignItems: 'center'
                  }}>
                    <Box>
                      <Box component="span" sx={{ fontWeight: 'medium' }}>
                        {pkg.name || 'Фасовка'}
                      </Box>
                      <Box component="span" sx={{ color: 'text.secondary', ml: 1 }}>
                        ({pkg.weight || 0}г)
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                      <Box sx={{ minWidth: 100, textAlign: 'right' }}>
                        {formatCurrency(pkg.price || 0)} × {pkg.count || 0} шт.
                      </Box>
                      <Box sx={{ minWidth: 100, textAlign: 'right', fontWeight: 'medium' }}>
                        {formatCurrency(pkg.totalAmount || 0)}
                      </Box>
                    </Box>
                  </Box>
                  {Array.isArray(pkg.cities) && pkg.cities.length > 0 && (
                    <Box sx={{ 
                      display: 'flex', 
                      flexWrap: 'wrap',
                      gap: 0.5,
                      mt: 0.5
                    }}>
                      {pkg.cities.map((city, cityIndex) => (
                        <Box
                          key={`${city || ''}-${cityIndex}`}
                          sx={{
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                            px: 1,
                            py: 0.25,
                            borderRadius: 1,
                            fontSize: '0.75rem'
                          }}
                        >
                          {city}
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              ))
            ) : (
              <Box>Нет данных о фасовках</Box>
            )}
          </Box>
        );
      }
    },
    { 
      field: 'totalItems', 
      headerName: 'Всего', 
      width: 150,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => {
        const row = params.row || {};
        return (
          <Box sx={{ textAlign: 'right', width: '100%' }}>
            <Box sx={{ fontWeight: 'medium' }}>{row.totalItems || 0} шт.</Box>
            <Box sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
              {formatCurrency(row.totalAmount || 0)}
            </Box>
          </Box>
        );
      }
    }
  ];

  return (
    <Fade in={true} timeout={250}>
      <Container maxWidth="xl" disableGutters sx={{
        pb: 4,
        minWidth: 0,
        '& .MuiPaper-root': { backgroundImage: 'none' },
        '& .MuiTypography-root': { fontVariantNumeric: 'tabular-nums' }
      }}>
        {/* Compact Hero Section */}
        <Paper
          elevation={0}
          sx={{
            bgcolor: 'transparent',
            py: { xs: 2, md: 3 },
            mb: 2,
            borderRadius: 0,
            borderBottom: 1,
            borderColor: 'divider',
            color: 'text.primary'
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
            <Box display="flex" alignItems="center" sx={{ minWidth: 0 }}>
              <Avatar variant="rounded" sx={{ bgcolor: 'action.selected', color: 'primary.main', border: '1px solid', borderColor: alpha(theme.palette.primary.main, 0.3), mr: 2, width: 48, height: 48, display: { xs: 'none', sm: 'flex' } }}>
                <DashboardIcon sx={{ fontSize: 24 }} />
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 750, letterSpacing: '-0.045em', fontSize: { xs: '1.65rem', md: '2.25rem' }, overflowWrap: 'anywhere' }}>
                  Обзор показателей
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Аналитика в реальном времени
                </Typography>
              </Box>
            </Box>
            <Tooltip title="Обновить данные">
              <IconButton
                aria-label="Обновить данные"
                onClick={handleRefresh}
                sx={{
                  color: 'primary.main',
                  bgcolor: 'background.paper',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1.5,
                  width: 44,
                  height: 44,
                  flexShrink: 0,
                  '&:hover': { bgcolor: 'action.hover', borderColor: 'primary.main' }
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Paper>

        <Alert 
          severity="info" 
          sx={{ 
            mb: 2,
            borderRadius: 1.5,
            py: 0.5,
            bgcolor: 'background.paper',
            color: 'text.secondary',
            border: 1,
            borderColor: 'divider',
            '& .MuiAlert-icon': { color: 'secondary.main' }
          }}
        >
          <strong>Совет:</strong> Для расчета зарплаты курьеров настройте зарплаты и штрафы в разделе фасовки
        </Alert>
        <Paper 
          elevation={0}
          sx={{
            p: { xs: 1.5, sm: 2.5 },
            mb: 3,
            borderRadius: 2,
            border: 1,
            borderColor: 'divider'
          }}
        >
          
          <Box sx={{ mb: 2.5, pb: 2.5, borderBottom: 1, borderColor: 'divider' }}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ru}>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                  {PERIOD_PRESETS.map((preset) => (
                    <Button
                      key={preset.label}
                      onClick={() => handlePeriodClick(preset)}
                      variant={activePeriod === preset.label ? 'contained' : 'outlined'}
                      size="small"
                      sx={{
                        borderRadius: 1,
                        minHeight: 36,
                        px: 1.5,
                        textTransform: 'none',
                        fontWeight: 600,
                        boxShadow: 'none',
                        ...(activePeriod === preset.label ? {
                          bgcolor: 'primary.main',
                          color: 'primary.contrastText',
                          '&:hover': { bgcolor: 'primary.light', boxShadow: 'none' }
                        } : {
                          borderColor: 'divider',
                          color: 'text.secondary',
                          '&:hover': {
                            borderColor: 'primary.main',
                            bgcolor: 'action.hover',
                            color: 'text.primary'
                          }
                        })
                      }}
                    >
                      {preset.label}
                    </Button>
                  ))}
                  
                  <Button
                    variant={activePeriod === 'Другой' ? 'contained' : 'outlined'}
                    color="secondary"
                    size="small"
                    startIcon={<CalendarTodayIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      minHeight: 36,
                      borderRadius: 1,
                      px: 1.5,
                      boxShadow: 'none',
                      fontWeight: 600,
                      borderColor: activePeriod === 'Другой' ? 'secondary.main' : 'divider',
                      '&:hover': { boxShadow: 'none' }
                    }}
                    onClick={handleCustomDateClick}
                  >
                    Период
                  </Button>
                </Box>

                <Popover
                  open={Boolean(anchorEl)}
                  anchorEl={anchorEl}
                  onClose={handleCustomDateClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  PaperProps={{
                    sx: {
                      mt: 1,
                      p: 2,
                      boxShadow: theme.shadows[8],
                      borderRadius: 2
                    }
                  }}
                >
                  <Stack spacing={2} sx={{ width: { xs: 'min(280px, calc(100vw - 64px))', sm: 560 } }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                      Выберите период
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Card elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                            Начало периода
                          </Typography>
                          <DateTimePicker
                            value={startDate}
                            onChange={(newValue) => {
                              setStartDate(newValue);
                              if (newValue > endDate) {
                                setEndDate(endOfDay(newValue));
                              }
                            }}
                            slotProps={{
                              textField: {
                                size: 'small',
                                variant: 'standard',
                                fullWidth: true
                              }
                            }}
                          />
                        </Card>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Card elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                            Конец периода
                          </Typography>
                          <DateTimePicker
                            value={endDate}
                            onChange={(newValue) => {
                              setEndDate(newValue);
                              if (newValue < startDate) {
                                setStartDate(startOfDay(newValue));
                              }
                            }}
                            slotProps={{
                              textField: {
                                size: 'small',
                                variant: 'standard',
                                fullWidth: true
                              }
                            }}
                          />
                        </Card>
                      </Grid>
                    </Grid>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button onClick={handleCustomDateClose} variant="contained">
                        Применить
                      </Button>
                    </Box>
                  </Stack>
                </Popover>

                <Typography variant="subtitle2" color="text.secondary" sx={{ px: 1 }}>
                  Период: {new Date(startDate).toLocaleDateString('ru-RU')} {new Date(startDate).toLocaleTimeString('ru-RU')} - {new Date(endDate).toLocaleDateString('ru-RU')} {new Date(endDate).toLocaleTimeString('ru-RU')}
                </Typography>
              </Stack>
            </LocalizationProvider>
          </Box>

          <Grid
            container
            spacing={1.5}
            sx={{ alignItems: 'stretch' }}
          >
            {[{
              title: 'Общая сумма',
              value: `${stats.totalSum} ₽`,
              icon: <MonetizationOnIcon />,
              color: theme.palette.primary.main,
            }, {
              title: 'К выплате',
              value: `${stats.totalBaseSum} ₽`,
              icon: <AccountBalanceWalletIcon />,
              color: theme.palette.success.main,
            }, {
              title: 'К выплате (сайт)',
              value: `${formatCurrency(stats.marketplaceSold.totalBaseSum)}`,
              icon: <AccountBalanceWalletIcon />,
              color: theme.palette.success.dark,
            }, {
              title: 'Купоны',
              value: `${stats.totalCuponSum} ₽`,
              icon: <LocalOfferIcon />,
              color: theme.palette.secondary.main,
            }, {
              title: 'Пользователи',
              value: stats.usersCount,
              icon: <PeopleIcon />,
              color: theme.palette.info.main,
            }, {
              title: 'Продажи',
              value: stats.ordersCount,
              icon: <ShoppingCartIcon />,
              color: theme.palette.warning.main,
            }, {
              title: 'Выдано адресов',
              value: issuedStats.totalCount,
              icon: <CheckCircleIcon />,
              color: theme.palette.secondary.main,
            }, {
              title: 'Сумма выданных',
              value: `${formatCurrency(issuedStats.totalAmount)}`,
              icon: <MonetizationOnIcon />,
              color: theme.palette.secondary.main,
            }].map(({ title, value, icon, color }, index) => (
              <Grid size={{ xs: 12, sm: 6, lg: 3 }} key={index} sx={{ minWidth: 0 }}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    bgcolor: 'background.default',
                    color: 'text.primary',
                    borderRadius: 1.5,
                    border: 1,
                    borderColor: index === 0 ? alpha(theme.palette.primary.main, 0.4) : 'divider',
                    boxShadow: index === 0 ? `inset 0 1px 0 ${alpha(theme.palette.primary.main, 0.35)}, 0 0 24px ${alpha(theme.palette.primary.main, 0.04)}` : 'none'
                  }}
                >
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={2.5}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: index === 0 ? 'inherit' : 'text.secondary',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          letterSpacing: '0.04em',
                          textTransform: 'uppercase'
                        }}
                      >
                        {title}
                      </Typography>
                      <Box sx={{ color: index === 0 ? 'inherit' : color, display: 'flex', flexShrink: 0 }}>
                        {React.cloneElement(icon, { sx: { fontSize: 20 } })}
                      </Box>
                    </Box>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 750,
                        fontSize: { xs: '1.65rem', xl: '1.85rem' },
                        letterSpacing: '-0.04em',
                        lineHeight: 1.2,
                        overflowWrap: 'anywhere'
                      }}
                    >
                      {value}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Crypto payments statistics */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
              ₿ Оплаты криптой (по токенам)
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 4 }}>
                <Card elevation={0} sx={{ p: 2.5, height: '100%', borderRadius: 1.5, bgcolor: 'background.default', border: 1, borderColor: 'divider' }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="body2" color="text.secondary">Всего оплат</Typography>
                    {cryptoLoading && <CircularProgress size={16} />}
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{cryptoStats.count}</Typography>
                  <Typography variant="caption" color="text.secondary">за выбранный период</Typography>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, md: 8 }}>
                <Card elevation={0} sx={{ p: 2.5, height: '100%', borderRadius: 1.5, bgcolor: 'background.default', border: 1, borderColor: 'divider' }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="body2" color="text.secondary">Сумма к выплате (base_price)</Typography>
                    {cryptoLoading && <CircularProgress size={16} />}
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{formatCurrency(cryptoStats.totalBase)}</Typography>
                  <Typography variant="caption" color="text.secondary">по всем токенам</Typography>
                </Card>
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mt: 1 }}>
              {(cryptoStats.rows || []).map((row) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={row.platform}>
                  <Card elevation={0} sx={{ p: 2, height: '100%', borderRadius: 1.5, border: 1, borderColor: 'divider' }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {row.platform}
                      </Typography>
                      <Chip size="small" label={`${row.count} шт.`} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{formatCurrency(row.totalBase)}</Typography>
                    <Typography variant="caption" color="text.secondary">base_price суммарно</Typography>
                  </Card>
                </Grid>
              ))}
              {(!cryptoStats.rows || cryptoStats.rows.length === 0) && !cryptoLoading && (
                <Grid size={12}>
                  <Alert severity="info">Нет данных об оплатах криптой за выбранный период</Alert>
                </Grid>
              )}
            </Grid>
          </Box>
          
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
              Статистика адресов
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
              <Card 
                elevation={2}
                sx={{ 
                  p: 1.5,
                  borderRadius: 1.5,
                  bgcolor: 'background.default',
                  border: 1,
                  borderColor: 'divider',
                  minWidth: 0,
                  flex: '1 1 220px',
                  boxShadow: 'none'
                }}
              >
                <Box display="flex" alignItems="center" mb={1}>
                  <PeopleIcon sx={{ mr: 1, color: theme.palette.primary.main, fontSize: 20 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                    Курьер
                  </Typography>
                </Box>
                <Select
                  value={selectedCourier}
                  onChange={(e) => setSelectedCourier(e.target.value)}
                  displayEmpty
                  variant="standard"
                  sx={{ 
                    width: '100%',
                    '& .MuiSelect-select': {
                      fontWeight: 'bold',
                      color: theme.palette.primary.main
                    }
                  }}
                >
                  <MenuItem value="">Все курьеры</MenuItem>
                  {couriers.map(courier => (
                    <MenuItem key={courier._id} value={courier._id}>
                      {courier.name}
                    </MenuItem>
                  ))}
                </Select>
              </Card>

              <Card 
                elevation={2}
                sx={{ 
                  p: 1.5,
                  borderRadius: 1.5,
                  bgcolor: 'background.default',
                  border: 1,
                  borderColor: 'divider',
                  minWidth: 0,
                  flex: '1 1 220px',
                  boxShadow: 'none'
                }}
              >
                <Box display="flex" alignItems="center" mb={1}>
                  <LocationCityIcon sx={{ mr: 1, color: theme.palette.secondary.main, fontSize: 20 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                    Город
                  </Typography>
                </Box>
                <Select
                  value={selectedCity}
                  onChange={(e) => setSelectedCity(e.target.value)}
                  displayEmpty
                  variant="standard"
                  sx={{ 
                    width: '100%',
                    '& .MuiSelect-select': {
                      fontWeight: 'bold',
                      color: theme.palette.secondary.main
                    }
                  }}
                >
                  <MenuItem value="">Все города</MenuItem>
                  {cities.map(city => (
                    <MenuItem key={city._id} value={city._id}>
                      {city.name}
                    </MenuItem>
                  ))}
                </Select>
              </Card>

              {addressLoading && (
                <Box display="flex" alignItems="center" justifyContent="center" sx={{ minWidth: 60 }}>
                  <CircularProgress size={24} />
                </Box>
              )}
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
              <PositionsPanel 
                title="Добавленные позиции" 
                data={addressStats.active} 
                isLoading={addressLoading}
                color={theme.palette.primary.main}
                icon={<CheckCircleIcon />}
              />
              <PositionsPanel 
                title="Проданные позиции" 
                data={addressStats.inactive} 
                isLoading={addressLoading}
                color={theme.palette.secondary.main}
                icon={<ShoppingCartIcon />}
              />
            </Box>
          </Box>

        </Paper>

        <Paper 
          elevation={0}
          sx={{
            p: { xs: 1.5, sm: 2.5 },
            mt: 3,
            borderRadius: 2,
            border: 1,
            borderColor: 'divider'
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
            Активные позиции
          </Typography>
          <Box sx={{ width: '100%' }}>
            {isProductsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : Array.isArray(productsStats) && productsStats.length > 0 ? (
              <Grid container spacing={3}>
                {productsStats.map((product, index) => (
                  <Grid size={12} key={product.productId}>
                      <Card 
                        elevation={0}
                        sx={{
                          borderRadius: 1.5,
                          bgcolor: 'background.paper',
                          border: 1,
                          borderColor: 'divider'
                        }}
                      >
                        {/* Шапка с названием товара */}
                        <Box sx={{ 
                          p: { xs: 2, sm: 2.5 },
                          bgcolor: 'action.hover',
                          borderBottom: 1,
                          borderColor: 'divider',
                          color: 'text.primary'
                        }}>
                          <Box display="flex" alignItems="center">
                            <Avatar 
                              sx={{ 
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                color: 'primary.main',
                                borderRadius: 1.5,
                                mr: 2,
                                width: 40,
                                height: 40 
                              }}
                            >
                              <ShoppingCartIcon />
                            </Avatar>
                            <Box>
                              <Typography variant="h6" component="div" fontWeight="bold">
                                {product.productName || 'Без названия'}
                              </Typography>
                              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                {product.packagings?.length || 0} фасовок • {product.totalItems || 0} шт.
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                        
                        {/* Тело с фасовками */}
                        <CardContent sx={{ p: 0 }}>
                          {Array.isArray(product.packagings) && product.packagings.length > 0 ? (
                            <Stack spacing={0}>
                              {product.packagings.map((pkg, pkgIndex) => (
                                <Paper 
                                  key={pkg.packagingId || pkgIndex}
                                  sx={{ 
                                    p: { xs: 1.5, sm: 2 },
                                    m: { xs: 1, sm: 2 },
                                    borderRadius: 1,
                                    bgcolor: 'background.default',
                                    border: 1,
                                    borderColor: 'divider',
                                    boxShadow: 'none'
                                  }}
                                >
                                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                                    <Box flex={1}>
                                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                                        {pkg.name || 'Фасовка'}
                                      </Typography>
                                      {Array.isArray(pkg.cities) && pkg.cities.length > 0 && (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                                          {pkg.cities.map((city, cityIndex) => (
                                            <Chip
                                              key={`${city || ''}-${cityIndex}`}
                                              label={city}
                                              size="small"
                                              sx={{ 
                                                height: 24, 
                                                fontSize: '0.75rem',
                                                bgcolor: alpha(theme.palette.secondary.main, 0.1),
                                                color: theme.palette.secondary.main,
                                                fontWeight: 'medium'
                                              }}
                                            />
                                          ))}
                                        </Box>
                                      )}
                                    </Box>
                                    
                                    <Box textAlign="right" sx={{ ml: 2 }}>
                                      <Typography variant="h5" fontWeight="bold" color="primary">
                                        {formatCurrency(Number(pkg.totalAmount) || 0)}
                                      </Typography>
                                      <Typography variant="body2" color="text.secondary">
                                        {pkg.totalVolume || 0} гр.
                                      </Typography>
                                    </Box>
                                  </Box>
                                  
                                  <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <Box display="flex" gap={2}>
                                      <Chip 
                                        label={`${formatCurrency(Number(pkg.price) || 0)}`}
                                        variant="outlined"
                                        size="small"
                                        sx={{ fontWeight: 'medium' }}
                                      />
                                      <Chip 
                                        label={`${pkg.count || 0} шт.`}
                                        color="primary"
                                        size="small"
                                        sx={{ fontWeight: 'bold' }}
                                      />
                                    </Box>
                                    
                                    <Typography variant="body2" color="text.secondary">
                                      {pkg.count || 0} × {formatCurrency(Number(pkg.price) || 0)}
                                    </Typography>
                                  </Box>
                                </Paper>
                              ))}
                              
                              {/* Итоговая строка */}
                              <Box sx={{ 
                                p: 2,
                                m: { xs: 1, sm: 2 },
                                mt: 0,
                                bgcolor: 'action.hover',
                                borderRadius: 1,
                                border: 1,
                                borderColor: 'divider'
                              }}>
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                  <Typography variant="h6" fontWeight="bold">
                                    Итого  
                                  </Typography>
                                  <Box display="flex" alignItems="center" gap={1}>
                                    <Typography variant="h6" fontWeight="bold">
                                      {product.totalItems || 0} шт.
                                    </Typography>
                                    <Typography variant="h5" fontWeight="bold" color="success.main">
                                      {formatCurrency(Number(product.totalAmount) || 0)}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            </Stack>
                          ) : (
                            <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                              <Typography>Нет данных о фасовках</Typography>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box 
                sx={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 8,
                  color: 'text.secondary'
                }}
              >
                <ShoppingCartIcon sx={{ mb: 2, fontSize: 32, color: 'text.secondary' }} />
                <Typography variant="h6" gutterBottom>Нет активных позиций</Typography>
                <Typography variant="body2">Добавьте товары для отображения статистики</Typography>
              </Box>
            )}
          </Box>
        </Paper>

        {/* Компактная статистика выданных адресов */}
        <Paper 
          elevation={0}
          sx={{
            p: { xs: 1.5, sm: 2.5 },
            mt: 3,
            borderRadius: 2,
            border: 1,
            borderColor: 'divider'
          }}
        >
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            Выданные адреса
          </Typography>
          {issuedLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : issuedStats.packagings.length > 0 ? (
            <Box>
              <Box sx={{ maxHeight: 320, overflowY: 'auto' }}>
              <Stack spacing={1}>
                {issuedStats.packagings.map((item, index) => (
                  <Box 
                    key={index}
                    sx={{ 
                      p: 1.5,
                      bgcolor: 'background.default',
                      borderRadius: 1,
                      border: 1,
                      borderColor: 'divider'
                    }}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          flexGrow: 1,
                          fontWeight: 'medium',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {item.productName} • {item.packagingName}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1} sx={{ flexShrink: 0 }}>
                        <Chip 
                          label={`${item.count} шт.`}
                          size="small"
                          color="secondary"
                          variant="outlined"
                          sx={{ minWidth: '60px' }}
                        />
                        <Typography variant="body2" sx={{ fontWeight: 'bold', minWidth: '80px', textAlign: 'right' }}>
                          {formatCurrency(Number(item.totalAmount) || 0)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Stack>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Box 
                sx={{ 
                  p: 1.5,
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                  border: 1,
                  borderColor: 'divider'
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle1" fontWeight="bold">
                    Итого выдано
                  </Typography>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Typography variant="body1" fontWeight="bold">
                      {issuedStats.totalCount} шт.
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" color="secondary.main">
                      {formatCurrency(issuedStats.totalAmount)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          ) : (
            <Box 
              sx={{ 
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 4,
                opacity: 0.5
              }}
            >
              <Typography variant="body2" color="text.secondary">Нет выданных адресов</Typography>
            </Box>
          )}
        </Paper>
      </Container>
    </Fade>
  );
};

export default Dashboard;